/**
 * <QuickActionTile> — one of the parent home's fixed action buttons (molecule).
 *
 * A pressable `<Card>` holding an `<IconBubble>` and a bold label, and nothing else: the
 * quick actions are the heart of the parent's self-service, so they carry a good word rather
 * than an explanatory subtitle. There is no `hint` / `description` prop on purpose.
 *
 * **À plat** : la tuile garde la surface blanche et son filet, sans ombre. Une page où toutes
 * les cartes décollent n'en met aucune en avant — le relief est réservé à la carte qui porte
 * l'information principale (voir `CLAUDE.md`, « Une seule surface en relief par page »).
 *
 * Composes DS pieces only — `<Card>`, `<IconBubble>`, `<Row>`, `<Text>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../Card"

/** Toutes les tuiles ont la même hauteur, que leur libellé tienne sur une ou deux lignes. */
export const TILE_MIN_HEIGHT = 72

export type QuickActionTileProps = {
    iconName: IconName
    label: string
    onPress?: () => void
    /**
     * Inactive : estompée, sans réponse au toucher, annoncée « désactivé » — et toujours à sa place.
     * Une tuile sans `onPress` l'est aussi : aucun geste ne lui est branché.
     */
    disabled?: boolean
    testID?: string
}

export function QuickActionTile(props: QuickActionTileProps) {
    const { iconName, label, onPress, disabled = false, testID } = props

    return (
        <Card
            pressable
            onPress={onPress}
            disabled={disabled || onPress == null}
            p="4"
            style={styles.tile}
            testID={testID}
        >
            <Row gap="3" align="center">
                <IconBubble iconName={iconName} />
                <Text weight="bold" size="text-md" style={styles.label}>
                    {label}
                </Text>
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    tile: {
        minHeight: TILE_MIN_HEIGHT,
        justifyContent: "center",
    },
    label: {
        flexShrink: 1,
    },
}))
